function normalizeTextForFingerprint(value: any): string {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[“”«»]/g, '"')
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeMediaForFingerprint(value: any): string {
  const raw = String(value || "").trim().toLowerCase();

  if (!raw) return "";

  try {
    const url = new URL(raw);
    const path = url.pathname.toLowerCase();

    // Use only the final filename segment to avoid domain/query variations
    const fileName = path.split("/").pop() || path;

    return fileName
      .replace(/\?.*$/, "")
      .replace(/-\d{10,}/g, "")
      .replace(/_\d{10,}/g, "")
      .trim();
  } catch {
    return raw
      .replace(/-\d{10,}/g, "")
      .replace(/_\d{10,}/g, "")
      .trim();
  }
}

export function buildCanonicalQuestionFingerprint(question: any): string {
  const competency = normalizeTextForFingerprint(
    question.competency || question.competence || question.type || ""
  );

  const section = normalizeTextForFingerprint(
    question.section || question.subsection || question.subType || ""
  );

  const title = normalizeTextForFingerprint(question.title);

  const instruction = normalizeTextForFingerprint(
    question.instruction ||
    question.instructions ||
    question.prompt ||
    ""
  );

  const questionText = normalizeTextForFingerprint(
    question.question ||
    question.questionText ||
    question.body ||
    question.enunciado ||
    ""
  );

  const correctAnswer = normalizeTextForFingerprint(
    question.correctAnswer ||
    question.answer ||
    question.correct ||
    ""
  );

  const options = Array.isArray(question.options) ? question.options : [];

  const optionParts = options
    .map((opt: any) => {
      const label = normalizeTextForFingerprint(opt.label || opt.id || opt.value);
      const text = normalizeTextForFingerprint(opt.text || opt.content || opt.title || "");
      const image = normalizeMediaForFingerprint(opt.image || opt.imageUrl || "");
      return `${label}:${text}:${image}`;
    })
    .sort()
    .join("||");

  const audio = normalizeMediaForFingerprint(
    question.audioUrl ||
    question.audio ||
    question.media?.audioUrl ||
    question.metadata?.audioUrl ||
    ""
  );

  return [
    competency,
    section,
    title,
    instruction,
    questionText,
    correctAnswer,
    optionParts,
    audio,
  ].join("###");
}

export async function buildQuestionFingerprintHash(question: any): Promise<string> {
  const fingerprint = buildCanonicalQuestionFingerprint(question);

  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprint);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);

  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");

  return hashHex;
}

export function getStableQuestionForFingerprint(
  q: any,
  pendingMedia?: any,
  resolvedAudioMappings?: any
): any {
  // Reconstruct options with stable asset names
  const safeOptions = Array.isArray(q.options) ? q.options : [];
  const options = safeOptions.map((opt: any) => {
    let image = opt.image || opt.imageUrl || "";
    const pendingFile = pendingMedia?.[q.temporaryImportId]?.optionImages?.[opt.label];
    if (pendingFile) {
      image = pendingFile.name;
    }
    return {
      ...opt,
      image,
      imageUrl: image
    };
  });

  // Get stable audio filename
  let audio = q.audioUrl || q.audio || "";
  const pendingAudioFile =
    pendingMedia?.[q.temporaryImportId]?.audioFile ||
    resolvedAudioMappings?.[q.temporaryImportId]?.file;

  if (pendingAudioFile) {
    audio = pendingAudioFile.name;
  } else if (q.associatedAudioName) {
    audio = q.associatedAudioName;
  }

  return {
    ...q,
    options,
    audioUrl: audio,
    audio
  };
}

export function normalizeCompetency(value: any): string {
  const v = String(value || "").trim().toLowerCase();
  if (
    v === "listening" ||
    v === "co" ||
    v.includes("compréhension orale") ||
    v.includes("comprehension orale") ||
    v.includes("listening") ||
    v.includes("orale") ||
    v.includes("oral")
  ) {
    return "listening";
  }
  if (
    v === "reading" ||
    v === "ce" ||
    v.includes("compréhension écrite") ||
    v.includes("comprehension ecrite") ||
    v.includes("reading")
  ) {
    return "reading";
  }
  if (
    v === "writing" ||
    v === "ee" ||
    v.includes("expression écrite") ||
    v.includes("expression ecrite") ||
    v.includes("writing")
  ) {
    return "writing";
  }
  return "";
}
